import * as React from 'react';
import { Avatar, ButtonBase, Chip, Container, Stack, styled, Typography } from '@mui/material';
import MuiBox from '@mui/material/Box';
import Slider from '@mui/material/Slider';

import { Canvas } from '@react-three/fiber';
import { OrbitControls, useGLTF } from '@react-three/drei';

import Bottom from '../Bottom';
import GaugeChart from '../../components/GaugeChart';

import iconCholesterol from '../../assets/icon/iconCholesterol.png';
import iconHeartRate from '../../assets/icon/iconHeartRate.png';
import iconWeightScale from '../../assets/icon/iconWeightScale.png';
import iconBlood from '../../assets/icon/iconBlood.png';

// TS2590 회피: 이 파일에서만 Box를 any로 사용
const Box = MuiBox as any;

const Img = styled('img')({
  margin: 'auto',
  display: 'block',
  maxWidth: '100%',
  maxHeight: '100%',
});

const marks = [
  { value: 2025, label: '2025' },
  { value: 2026, label: '2026' },
  { value: 2027, label: '2027' },
  { value: 2028, label: '2028' },
];

function MyModel() {
  const { scene } = useGLTF('/model/간_간경변.gltf');
  return <primitive object={scene} scale={3} />;
}

function ModelViewer() {
  return (
    <Canvas style={{ height: 250 }}>
      <OrbitControls />
      <ambientLight intensity={1} />
      <MyModel />
    </Canvas>
  );
}

const ModelingDetail = () => {
  return (
    <React.Fragment>
      <Box>
        <Container sx={{ p: '0 !important', pb: '50px !important', bgcolor: '#ECF0F2', minHeight: '100vh', minWidth: 320 }}>
          <Box sx={{ bgcolor: '#dcfdd4ff', borderRadius: '0 0 40px 40px', display: 'flex', p: 0 }}>
            <Box position="fixed" width="100%" bgcolor="#fff" zIndex={999} maxWidth="1200px">
              <Stack direction="row">
                <Box>
                  <ButtonBase sx={{ width: 180, height: 50, ml: 1 }}>
                    <Img alt="Dual" src="/dual.png" />
                  </ButtonBase>
                </Box>
              </Stack>
            </Box>
          </Box>

          <Box sx={{ mx: 2, mt: 7 }}>
            <Box>
              <ModelViewer />
            </Box>

            <Box
              sx={{
                mt: 8,
                mb: 2,
                mx: 2,
                '& .MuiSlider-thumb': { width: '28px', height: '28px', bgcolor: '#628CFA' },
                '& .MuiSlider-rail': { bgcolor: '#f4fbff', opacity: 1, height: '17px', boxShadow: '0px 5px 10px rgba(192, 211, 221, .85) ' },
                '& .MuiSlider-track': { background: 'linear-gradient(90deg, #A8B5F0 0%, #628CFA 100%)', border: 0, height: '17px' },
                '& .MuiSlider-valueLabelOpen': { bgcolor: '#628CFA', px: 1 },
                '& .MuiSlider-valueLabelLabel': { fontSize: '12px' },
                '& .MuiSlider-markLabel': { fontSize: '12px' },
              }}
            >
              <Slider
                aria-label="Always visible"
                defaultValue={2025}
                step={1}
                marks={marks}
                valueLabelDisplay="on"
                max={2028}
                min={2025}
              />
            </Box>

            <Box sx={{ border: '1px solid #fff', borderRadius: 5, boxShadow: '4px 4px 8px #D9D9D9', p: 3, mb: 4, bgcolor: '#E8ECF1' }}>
              <Box
                sx={{
                  width: '100%',
                  height: 150,
                  '& svg': { width: '100% !important', height: '100% !important' },
                }}
              >
                <GaugeChart />
              </Box>

              <Stack
                direction="row"
                justifyContent="space-between"
                spacing={2}
                sx={{
                  '& .chartBox': {
                    bgcolor: '#F3F6FA',
                    border: '1px solid #fff',
                    boxShadow: 'inset 4px 4px 4px  rgba(192, 211, 221, .5)',
                    borderRadius: 2,
                    px: 2,
                    py: 1,
                  },
                  '& .chartBox .chartBoxTitle': { fontSize: 14 },
                  '& .chartBox .chartBoxText': { textAlign: 'right', mt: 2, fontWeight: 700, color: '#cb0808' },
                }}
              >
                <Box className="chartBox">
                  <Typography className="chartBoxTitle">3년내 발병율</Typography>
                  <Typography className="chartBoxText">0.0123%</Typography>
                </Box>
                <Box className="chartBox">
                  <Typography className="chartBoxTitle">건강 발병 위험성</Typography>
                  <Typography className="chartBoxText">3위</Typography>
                </Box>
                <Box className="chartBox">
                  <Typography className="chartBoxTitle">건강 상태 위험도</Typography>
                  <Typography className="chartBoxText">2배 높음</Typography>
                </Box>
              </Stack>
            </Box>

            <Stack
              direction="row"
              spacing={2}
              sx={{
                justifyContent: 'space-between',
                alignItems: 'center',
                '& .disease': { bgcolor: '#fff', width: 90, py: 0.5, borderRadius: 4, textAlign: 'center' },
                '& p': { fontSize: '14px' },
                '& .diseaseIcon': { width: 26, height: 26, display: 'block', m: '5px auto' },
                '& .diseaseChip': { width: '80%', height: 21, display: 'block', m: 'auto' },
              }}
            >
              <Box className="disease">
                <Typography>고혈압</Typography>
                <Avatar alt="" src={iconBlood} variant="square" className="diseaseIcon" />
                <Chip color="warning" size="small" label="주의" className="diseaseChip" />
              </Box>

              <Box className="disease">
                <Typography>당뇨병</Typography>
                <Avatar alt="" src={iconHeartRate} variant="square" className="diseaseIcon" />
                <Chip color="error" size="small" label="나쁨" className="diseaseChip" />
              </Box>

              <Box className="disease">
                <Typography>비만</Typography>
                <Avatar alt="" src={iconWeightScale} variant="square" className="diseaseIcon" />
                <Chip color="success" size="small" label="좋음" className="diseaseChip" />
              </Box>

              <Box className="disease">
                <Typography>이상지질</Typography>
                <Avatar alt="" src={iconCholesterol} variant="square" className="diseaseIcon" />
                <Chip color="info" size="small" label="보통" className="diseaseChip" />
              </Box>
            </Stack>
          </Box>
        </Container>

        <Bottom />
      </Box>
    </React.Fragment>
  );
};

export default ModelingDetail;
